import { goToPostAuthDestination, resolvePostAuthDestination, savePostLoginRedirect } from "@utils/postAuthRedirect";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { Modal } from "@shared/controls/Modal";
import { InputComponent } from "@shared/forms";
import { PrimaryButton } from "@shared/buttons";
import { ErrorDisplay, SuccessDisplay } from "@shared/notifications";
import { SelectDropdown } from "@shared/forms/SelectComponent";
import Logo from "@shared/Logo";
import { toast, ToastContainer } from "react-toastify";
import { addCookie } from "@utils/cookies";
import {
  login,
  register,
  verifyAean,
  verifyNiesv,
  getProfile,
  requestSignupOTP,
  verifySignupOTP,
  googleSignup,
} from "@services/krent.service";
import { GoogleSignIn } from "@shared/GoogleSignIn";
import {
  persistGoogleSession,
  GoogleAuthResult,
} from "@utils/completeGoogleAuth";
import { OtpStep } from "@pages/SignUp/OtpModal";
import { BuyerSignupFlow } from "./BuyerSignupFlow";
import { accountTypes } from "../constants/accountTypes";
import {
  getPasswordValidationError,
  isPasswordValid,
  PASSWORD_REQUIREMENTS_HINT,
} from "@utils/passwordValidation";
import { getPhoneValidationError } from "@utils/phoneValidation";

interface GetStartedModalProps {
  show: boolean;
  onClose: () => void;
  onOpenLogin?: () => void;
  initialStep?: "selection" | "sub-selection" | "signup" | "otp";
  initialAccountType?: string;
  hideBackButton?: boolean;
}

export const GetStartedModal = ({
  show,
  onClose,
  onOpenLogin,
  initialStep,
  initialAccountType,
  hideBackButton,
}: GetStartedModalProps) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (show && /^\/property\/[^/]+\/?$/.test(window.location.pathname)) {
      savePostLoginRedirect();
    }
  }, [show]);

  const resumeAfterSignup = (profileUser?: any) => {
    if (!sessionStorage.getItem("pendingBooking") && !localStorage.getItem("postLoginRedirect")) return false;
    goToPostAuthDestination(navigate, resolvePostAuthDestination(profileUser));
    return true;
  };

  const location = useLocation();

  // Account selection state
  const [step, setStep] = useState<
    "selection" | "sub-selection" | "signup" | "otp"
  >(initialStep || "selection");
  const [selectedType, setSelectedType] = useState(initialAccountType || "");

  // Sign up form state (for non-buyer accounts)
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [preferredName, setPreferredName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [userNameError, setUserNameError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [referral, setReferral] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [isAssociationMember, setIsAssociationMember] = useState("");
  const [estateNumber, setEstateNumber] = useState("");
  const [niesvError, setNiesvError] = useState("");
  const [niesvVerified, setNiesvVerified] = useState(false);
  const [aeanError, setAeanError] = useState("");
  const [aeanVerified, setAeanVerified] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [togglePassword, setTogglePassword] = useState(false);
  const signupScrollRef = useRef<HTMLDivElement>(null);

  const passwordValidationError = getPasswordValidationError(password);
  const passwordIsValid = isPasswordValid(password);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!show) {
      setStep("selection");
      setSelectedType("");
      setFirstname("");
      setLastname("");
      setPreferredName("");
      setPhoneNumber("");
      setRegNumber("");
      setEmail("");
      setPassword("");
      setAgree(false);
      setError("");
      setUserNameError("");
      setSuccess("");
      setReferral("");
      setSelectedOption("");
      setIsAssociationMember("");
      setEstateNumber("");
      setNiesvError("");
      setNiesvVerified(false);
      setAeanError("");
      setAeanVerified(false);
    } else {
      // When modal opens, use initial values if provided
      if (initialStep) {
        setStep(initialStep);
      }
      if (initialAccountType) {
        setSelectedType(initialAccountType);
      }
    }
  }, [show, initialStep, initialAccountType]);

  useEffect(() => {
    if (!show || step !== "signup" || !error) return;
    signupScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [error, show, step]);

  const handleBackToSelection = () => {
    setStep("selection");
    setSelectedType("");
  };

  const handleBackToSignup = () => {
    setStep("signup");
  };

  const handleCardSelect = (id: string) => {
    setSelectedType(id);
  };

  const handleProceed = () => {
    if (
      selectedType === "seller" ||
      selectedType === "agent" ||
      selectedType === "property-manager"
    ) {
      setStep("sub-selection");
      setSelectedType("");
    } else {
      setStep("signup");
    }
  };

  const handleSubTypeSelect = (subType: string) => {
    setSelectedType(subType);
  };

  const handleProceedToSignup = () => {
    if (selectedType) {
      setStep("signup");
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOption(e.target.value);
    if (e.target.value !== "yes") {
      setRegNumber("");
      setNiesvError("");
      setNiesvVerified(false);
    }
  };

  const handleAssociationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setIsAssociationMember(e.target.value);
    if (e.target.value !== "yes") {
      setEstateNumber("");
      setAeanError("");
      setAeanVerified(false);
    }
  };

  const validateNiesv = async () => {
    try {
      const niesvNumber = regNumber;
      await verifyNiesv(niesvNumber);
      setNiesvVerified(true);
      setNiesvError("");
      return true;
    } catch (err) {
      setNiesvVerified(false);
      setNiesvError(typeof err === "string" ? err : "Invalid NIESV number");
      return false;
    }
  };

  const validateAean = async () => {
    try {
      const aeanNumber = estateNumber;
      await verifyAean(aeanNumber);
      setAeanVerified(true);
      setAeanError("");
      return true;
    } catch (err) {
      setAeanVerified(false);
      setAeanError(typeof err === "string" ? err : "Invalid AEAN number");
      return false;
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setNiesvError("");
    setAeanError("");

    try {
      const passwordError = passwordValidationError;
      const phoneError = getPhoneValidationError(phoneNumber);

      const validations = [
        {
          condition: !firstname.trim(),
          message: "Please enter your first name",
        },
        { condition: !lastname.trim(), message: "Please enter your last name" },
        {
          condition: !email.trim(),
          message: "Please enter your email address",
        },
        {
          condition: Boolean(phoneError),
          message: phoneError || "Please enter a valid phone number",
        },
        {
          condition: Boolean(passwordError),
          message: passwordError || "",
        },
        ...(selectedType !== "seller" && selectedType !== "property-manager"
          ? [
              {
                condition: !preferredName.trim(),
                message: "Please choose a username",
              },
            ]
          : []),
      ];

      for (const { condition, message } of validations) {
        if (condition) {
          setError(message);
          setLoading(false);
          return;
        }
      }

      if (selectedType !== "developer" && !agree) {
        setError("Please accept the terms and conditions to continue");
        setLoading(false);
        return;
      }

      if (
        selectedType !== "seller" &&
        selectedType !== "property-manager" &&
        (preferredName === firstname || preferredName === lastname)
      ) {
        setUserNameError(
          "Username must be different from your first and last name",
        );
        setLoading(false);
        return;
      }

      if (selectedType === "agent") {
        if (selectedOption === "yes") {
          const isNiesvValid = await validateNiesv();
          if (!isNiesvValid) {
            setLoading(false);
            return;
          }
        }

        if (isAssociationMember === "yes") {
          const isAeanValid = await validateAean();
          if (!isAeanValid) {
            setLoading(false);
            return;
          }
        }

        try {
          await requestSignupOTP(email);
          setStep("otp");
        } catch (otpErr: any) {
          setError(
            otpErr?.response?.data?.errors?.[0]?.message ||
              otpErr?.message ||
              "Failed to send OTP. Please try again.",
          );
        } finally {
          setLoading(false);
        }
        return;
      }

      // Add OTP flow for seller and property-manager as well
      if (selectedType === "seller" || selectedType === "property-manager") {
        try {
          await requestSignupOTP(email);
          setStep("otp");
        } catch (otpErr: any) {
          setError(
            otpErr?.response?.data?.errors?.[0]?.message ||
              otpErr?.message ||
              "Failed to send OTP. Please try again.",
          );
        } finally {
          setLoading(false);
        }
        return;
      }

      const data = {
        firstname,
        lastname,
        email,
        phone: phoneNumber,
        password,
        passwordConfirm: password,
        ...(selectedType !== "seller" && selectedType !== "property-manager" ? { preferredName } : {}),
        niesvNumber:
          selectedType === "agent" && selectedOption === "yes"
            ? regNumber
            : undefined,
        aeanNumber:
          selectedType === "agent" && isAssociationMember === "yes"
            ? estateNumber
            : undefined,
        role:
          selectedType === "buyer"
            ? "user"
            : selectedType === "developer"
              ? "developer"
              : selectedType === "seller"
                ? "landlord"
                : selectedType === "property-manager"
                  ? "property-manager"
                  : "agent",
        socialMediaSource: referral,
      };

      if (selectedType === "developer") {
        localStorage.setItem(
          "krent-signup-developer-data",
          JSON.stringify(data),
        );
        onClose();
        navigate("/get-started/developer-upload");
      } else {
        const registerRes = await register(data);

        if (registerRes.success) {
          setSuccess(registerRes.message || "Registration successful");

          try {
            const loginRes = await login(email, password);

            if (loginRes?.token) {
              addCookie("1Q_SPA", loginRes.token, 24000);

              const abortController = new AbortController();
              const profileRes: any = await getProfile(abortController.signal);

              if (profileRes?.data) {
                sessionStorage.setItem("user", JSON.stringify(profileRes.data));
              }

              const redirectPath =
                loginRes.role === "user"
                  ? "/explore-listings"
                  : loginRes.role === "developer"
                    ? "/developer-dashboard"
                    : "/overview";

              onClose();
              if (resumeAfterSignup(profileRes?.data || profileRes)) return;
              navigate(redirectPath, {
                replace: true,
                state: { from: location, verified: true },
              });
            }
          } catch (loginErr) {
            toast.success(
              "Registration successful! Please sign in with your credentials.",
            );
            onClose();
            navigate("/?auth=login", {
              state: {
                registrationSuccess: true,
                email: email,
              },
            });
          }
        }
      }
    } catch (err: any) {
      if (err?.errors && Array.isArray(err.errors)) {
        const errorMessages = err.errors.map((error: any) => {
          const message = error.message || error;
          if (message.toLowerCase().includes("email already in use")) {
            return "This email is already registered. Please use a different email or sign in.";
          } else if (message.toLowerCase().includes("phone")) {
            return "Please check your phone number format.";
          } else if (message.toLowerCase().includes("password")) {
            return "Please ensure your password meets the requirements.";
          } else if (message.toLowerCase().includes("username")) {
            return "Please choose a different username.";
          }
          return message;
        });
        setError(errorMessages.join(", "));
      } else {
        const errorMessage =
          typeof err === "string"
            ? err
            : err?.message || "Something went wrong. Please try again.";
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
      setTimeout(() => {
        setUserNameError("");
      }, 3000);
    }
  };

  const handleOtpVerification = async (otpCode: string) => {
    setVerifyingOtp(true);
    try {
      await verifySignupOTP(email, otpCode);

      const data = {
        firstname,
        lastname,
        email,
        phone: phoneNumber,
        password,
        passwordConfirm: password,
        ...(selectedType !== "seller" && selectedType !== "property-manager" ? { preferredName } : {}),
        niesvNumber:
          selectedType === "agent" && selectedOption === "yes"
            ? regNumber
            : undefined,
        aeanNumber:
          selectedType === "agent" && isAssociationMember === "yes"
            ? estateNumber
            : undefined,
        role:
          selectedType === "seller"
            ? "landlord"
            : selectedType === "property-manager"
              ? "property-manager"
              : "agent",
        socialMediaSource: referral,
      };

      const registerRes = await register(data);

      if (registerRes.success) {
        setSuccess(registerRes.message || "Registration successful");

        try {
          const loginRes = await login(email, password);

          if (loginRes?.token) {
            addCookie("1Q_SPA", loginRes.token, 24000);

            const abortController = new AbortController();
            const profileRes: any = await getProfile(abortController.signal);

            if (profileRes?.data) {
              sessionStorage.setItem("user", JSON.stringify(profileRes.data));
            }

            onClose();
            if (resumeAfterSignup(profileRes?.data || profileRes)) return;
            navigate("/overview", {
              replace: true,
              state: { from: location, verified: true },
            });
          }
        } catch (loginErr) {
          toast.success(
            "Registration successful! Please sign in with your credentials.",
          );
          onClose();
          navigate("/?auth=login", {
            state: {
              registrationSuccess: true,
              email: email,
            },
          });
        }
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.errors?.[0]?.message ||
          err?.message ||
          "OTP verification failed. Please try again.",
      );
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleGoogleSignup = async (response: any) => {
    try {
      setError("");

      if (!selectedType) {
        setError("Please select a role to continue with Google signup.");
        return;
      }

      const roleMapping: { [key: string]: string } = {
        seller: "landlord",
        "property-manager": "property-manager",
      };

      const role = roleMapping[selectedType];
      if (!role) {
        setError("Invalid role selected. Please try again.");
        return;
      }

      const registerRes = (await googleSignup({
        id_token: response.credential,
        role,
      })) as GoogleAuthResult;

      if (!registerRes.success || !registerRes.token) {
        throw new Error("Google signup failed");
      }

      setSuccess("Account created successfully with Google!");
      const profileUser = await persistGoogleSession(registerRes);
      onClose();
      if (resumeAfterSignup(profileUser || registerRes)) return;
      navigate("/overview", { replace: true });
    } catch (err: any) {
      const errorMessage =
        err?.message || "Google signup failed. Please try again.";
      setError(errorMessage);
    }
  };

  const referrals = [
    { value: "customer-service", label: "Krent Customer Service" },
    { value: "agent", label: "Agent" },
    { value: "instagram", label: "Instagram" },
    { value: "x", label: "X" },
    { value: "facebook", label: "Facebook" },
    { value: "google", label: "Google" },
  ];
  const dropdownOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];
  const associationDropdownOptions = [
    { value: "yes", label: "Yes, I am" },
    { value: "no", label: "No" },
  ];

  const getModalContent = () => {
    if (step === "selection") {
      return (
        <div className="px-4 pb-3 flex flex-col">
          <div className="mb-4">
            <div className=" justify-content-between align-items-center mb-2 gap-3">
              <Logo />

              <h4 className="fw-semibold font-onest mb-0 mt-2">
                Select account type
              </h4>
            </div>
            <p className="text-muted fs-14 mb-4 font-onest ">
              Let us know what you'll be using Krent for so we can optimize your
              experience.
            </p>
          </div>

          <div className="d-flex flex-column gap-4 mb-4 ">
            {accountTypes.map((account) => {
              const isSelected = selectedType === account.id;
              return (
                <div
                  key={account.id}
                  onClick={() => handleCardSelect(account.id)}
                  className="d-flex align-items-start gap-3 p-3 rounded-4"
                  style={{
                    border: `1.5px solid ${isSelected ? "#1a7a5e" : "#e0e0e0"}`,
                    cursor: "pointer",
                    background: isSelected ? "#f0faf6" : "#fff",
                    transition: "border-color 0.2s, background 0.2s",
                  }}
                >
                  <div
                    className="mt-1 justify-content-start rounded-2 flex-shrink-0"
                    style={{ width: 30, height: 48 }}
                  >
                    {account.icon}
                  </div>
                  <div>
                    <h6 className="fw-bold font-onest mb-1">{account.label}</h6>
                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                      {account.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            className="btn w-full fw-semibold"
            disabled={!selectedType}
            onClick={handleProceed}
            style={{
              background: selectedType ? "#47BBA9" : "#ccc",
              color: "#fff",
              borderRadius: 10,
              padding: "12px 0",
              border: "none",
              transition: "background 0.2s",
            }}
          >
            Proceed
          </button>

          <div className="text-center mt-3">
            <p className="mb-0 font-onest" style={{ fontSize: "14px" }}>
              Have a Krent account?{" "}
              <span
                onClick={() => {
                  onClose();
                  onOpenLogin?.();
                }}
                style={{
                  color: "#47BBA9",
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontWeight: "600",
                }}
              >
                Sign in here
              </span>
            </p>
          </div>
        </div>
      );
    }

    if (step === "sub-selection") {
      return (
        <div className="px-4 pt-0 pb-3">
          <div className="  mb-4">
            <Logo />

            <div className="mt-2">
              <button
                onClick={handleBackToSelection}
                className="btn btn-link text-decoration-none p-0"
                style={{ color: "#47BBA9" }}
              >
                ← Back
              </button>
            </div>
          </div>

          <h6 className="fw-semibold fs-16 mb-4">
            Are you a Property Owner or Property Manager?
          </h6>

          {[
            // { value: "agent", label: "Agent" },
            { value: "seller", label: "Property Owner" },
            { value: "property-manager", label: "Property Manager" },
          ].map((opt) => (
            <div
              key={opt.value}
              onClick={() => handleSubTypeSelect(opt.value)}
              className="d-flex align-items-center gap-3 p-3 rounded-3 mb-3"
              style={{
                border: `1.5px solid ${selectedType === opt.value ? "#1a7a5e" : "#e0e0e0"}`,
                cursor: "pointer",
                background: selectedType === opt.value ? "#f0faf6" : "#fff",
                transition: "border-color 0.2s, background 0.2s",
              }}
            >
              <div
                className="rounded-circle border flex-shrink-0"
                style={{
                  width: 18,
                  height: 18,
                  borderColor: selectedType === opt.value ? "#1a7a5e" : "#aaa",
                  borderWidth: 2,
                  borderStyle: "solid",
                  background:
                    selectedType === opt.value ? "#1a7a5e" : "transparent",
                }}
              />
              <span className="fw-semibold">{opt.label}</span>
            </div>
          ))}

          <button
            onClick={handleProceedToSignup}
            className="w-100 btn btn-primary rounded-3 py-2 mt-2"
            style={{ backgroundColor: "#1a7a5e", border: "none" }}
            disabled={!selectedType}
          >
            Continue
          </button>

          <div className="text-center my-4">
            <span className="text-muted fs-14">OR</span>
          </div>

          <div className="d-grid">
            <div
              className="d-flex align-items-center justify-content-center p-2 rounded-3 position-relative"
              style={{
                minHeight: "52px",
                backgroundColor: "#ffffff",
                overflow: "hidden",
              }}
            >
              {selectedType ? (
                <GoogleSignIn
                  onSuccess={handleGoogleSignup}
                  onError={setError}
                  buttonId="google-signin-button-subselection"
                  text="signup_with"
                  width={400}
                  enabled={show && step === "sub-selection" && !!selectedType}
                />
              ) : (
                <div className="text-muted fs-12">
                  Select a role to continue
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (step === "signup") {
      if (selectedType === "buyer") {
        return (
          <BuyerSignupFlow
            onClose={onClose}
            onBackToSelection={
              !hideBackButton ? handleBackToSelection : undefined
            }
            hideBackButton={hideBackButton}
          />
        );
      }

      // Original signup form for other account types
      return (
        <div
          ref={signupScrollRef}
          className="px-4 py-3"
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          <Logo />

          <div className="d-flex justify-content-between align-items-center mb-3">
            {!hideBackButton && (
              <button
                onClick={handleBackToSelection}
                className="btn btn-link text-decoration-none p-0"
                style={{ color: "#47BBA9" }}
              >
                ← Back
              </button>
            )}
          </div>

          <h4 className="fw-bolder font-onest mb-2">
            {selectedType === "developer"
              ? "Create a developer account"
              : selectedType === "agent"
                ? "Create an agent account"
                : selectedType === "property-manager"
                  ? "Create a property manager account"
                  : "Create a property owner account"}
          </h4>

          <ErrorDisplay
            errors={
              Array.isArray(error)
                ? error
                : error
                  ? [{ message: String(error), field: "general" }]
                  : []
            }
          />
          <SuccessDisplay message={success} />

          <form className="mb-children mt-3" onSubmit={handleSubmit}>
            <div className="">
              <div className=" mb-3">
                <InputComponent
                  label="First Name"
                  placeholder="Enter your first name"
                  type="text"
                  value={firstname}
                  change={(e) => setFirstname(e.target.value)}
                  required
                />
              </div>
              <div className=" mb-3">
                <InputComponent
                  label="Last Name"
                  placeholder="Enter your last name"
                  type="text"
                  value={lastname}
                  change={(e) => setLastname(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="row">
              {selectedType !== "seller" && selectedType !== "property-manager" && (
                <div className=" mb-3">
                  <InputComponent
                    label="Username"
                    placeholder="Choose a username"
                    type="text"
                    value={preferredName}
                    change={(e) => setPreferredName(e.target.value)}
                    required
                  />
                  {userNameError ? (
                    <small className="text-danger">{userNameError}</small>
                  ) : null}
                </div>
              )}
              <div className=" mb-3">
                <InputComponent
                  label="Phone Number"
                  placeholder="Enter your phone number"
                  icon="Call"
                  type="phoneNumber"
                  value={phoneNumber}
                  change={(e) => setPhoneNumber(e.target.value)}
                  required
                  length={11}
                />
              </div>
            </div>
            <div className="">
              <div className=" mb-3">
                <InputComponent
                  label="Email Address"
                  placeholder="Enter your email address"
                  icon="Message"
                  type="email"
                  value={email}
                  change={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className=" mb-3">
                <InputComponent
                  label="Password"
                  placeholder="Create a password"
                  icon="Lock"
                  type={togglePassword ? "text" : "password"}
                  value={password}
                  change={(e) => setPassword(e.target.value)}
                  required
                  passwordIcon={togglePassword ? "open" : "closed"}
                  onPasswordIconClick={() => setTogglePassword(!togglePassword)}
                />
                <small
                  className={`d-block mt-1 ${passwordIsValid ? "text-muted" : "text-danger"}`}
                >
                  {passwordValidationError || PASSWORD_REQUIREMENTS_HINT}
                </small>
              </div>
            </div>

            {selectedType === "agent" && (
              <>
                <div className="">
                  <div className=" mb-3">
                    <SelectDropdown
                      label="Are you a registered NIESV member?"
                      options={dropdownOptions}
                      value={selectedOption}
                      change={handleSelectChange}
                      required
                    />
                  </div>
                  {selectedOption === "yes" && (
                    <div className="mb-3">
                      <InputComponent
                        label="NIESV Registration Number"
                        placeholder="Enter your NIESV number"
                        type="text"
                        value={regNumber}
                        change={(e) => setRegNumber(e.target.value)}
                        required
                      />
                      {niesvError ? (
                        <small className="text-danger">{niesvError}</small>
                      ) : null}
                      {selectedOption === "yes" && !niesvVerified ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary mt-2"
                          onClick={validateNiesv}
                        >
                          Verify NIESV
                        </button>
                      ) : null}
                      {niesvVerified ? (
                        <small className="text-success">✓ NIESV verified</small>
                      ) : null}
                    </div>
                  )}
                </div>

                <div className="row">
                  <div className="mb-3">
                    <SelectDropdown
                      label="Are you a registered AEAN member?"
                      options={associationDropdownOptions}
                      value={isAssociationMember}
                      change={handleAssociationChange}
                      required
                    />
                  </div>
                  {isAssociationMember === "yes" && (
                    <div className=" mb-3">
                      <InputComponent
                        label="AEAN Registration Number"
                        placeholder="Enter your AEAN number"
                        type="text"
                        value={estateNumber}
                        change={(e) => setEstateNumber(e.target.value)}
                        required
                      />
                      {aeanError ? (
                        <small className="text-danger">{aeanError}</small>
                      ) : null}
                      {isAssociationMember === "yes" && !aeanVerified ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary mt-2"
                          onClick={validateAean}
                        >
                          Verify AEAN
                        </button>
                      ) : null}
                      {aeanVerified ? (
                        <small className="text-success">✓ AEAN verified</small>
                      ) : null}
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="">
              <div className=" mb-3">
                <SelectDropdown
                  label="How did you hear about us?"
                  options={referrals}
                  value={referral}
                  change={(e: any) => setReferral(e.target.value)}
                />
              </div>
              <div className=" mb-3">
                <div className="form-check mt-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="agree"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                    required
                  />
                  <p className="mt-4 fs-14">
                    By signing up you agree to our{" "}
                    <span
                      onClick={() => {
                        onClose();
                        navigate("/terms");
                      }}
                      className="text-primary fw-bold"
                      style={{ cursor: "pointer" }}
                    >
                      Terms of service
                    </span>{" "}
                    and{" "}
                    <span
                      onClick={() => {
                        onClose();
                        navigate("/privacy");
                      }}
                      className="text-primary fw-bold"
                      style={{ cursor: "pointer" }}
                    >
                      Privacy policy
                    </span>
                    .
                  </p>
                </div>
              </div>
            </div>

            <div className="d-grid">
              <PrimaryButton
                type="primary"
                text={
                  selectedType === "developer" || selectedType === "agent"
                    ? "Continue"
                    : "Create Account"
                }
                loading={loading}
                disable={!passwordIsValid}
              />
            </div>
          </form>
          <p className="text-light-black text-center my-4 my-md-3 fs-14">
            Already have an account?{" "}
            <span
              onClick={() => {
                onClose();
                onOpenLogin?.();
              }}
              className="text-link"
              style={{ cursor: "pointer", color: "#47BBA9" }}
            >
              Sign In
            </span>
          </p>
        </div>
      );
    }

    if (step === "otp") {
      return (
        <OtpStep
          email={email}
          onVerify={handleOtpVerification}
          onBack={handleBackToSignup}
          loading={verifyingOtp}
          type="signup"
        />
      );
    }

    return <div className="px-4 py-3" />;
  };

  return (
    <>
      <ToastContainer limit={1} />
      <Modal show={show} onClose={onClose} size={500} id="getStartedModal">
        {getModalContent()}
      </Modal>
    </>
  );
};
